import { FormEvent, useEffect, useState } from "react";
import { Copy, Edit, Plus, RefreshCw, Search, Send, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import {
  AdminUser,
  PaginationMeta,
  getCurrentAdmin,
  logout,
  AdminNewsletterCampaign,
  NewsletterCampaignStatus,
  deleteAdminNewsletterCampaign,
  listAdminNewsletterCampaigns,
  saveAdminNewsletterCampaign,
} from "./api";
import { useConfirmDialog } from "./components/ConfirmDialog";
import Pagination from "./components/Pagination";

const defaultPagination: PaginationMeta = {
  Page: 1,
  PerPage: 20,
  Total: 0,
  TotalPages: 1,
};

const statusLabels: Record<NewsletterCampaignStatus, string> = {
  Draft: "草稿",
  Sending: "寄送中",
  Sent: "已寄出",
};

const statusBadgeClass: Record<NewsletterCampaignStatus, string> = {
  Draft: "bg-[#eeeeee] text-[#59635d]",
  Sending: "bg-[#fff6df] text-[#7a5610]",
  Sent: "bg-[#e9f4ec] text-[#245438]",
};

export default function AdminNewsletterCampaigns() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [items, setItems] = useState<AdminNewsletterCampaign[]>([]);
  const [counts, setCounts] = useState({ Draft: 0, Sending: 0, Sent: 0 });
  const [pagination, setPagination] = useState<PaginationMeta>(defaultPagination);
  const [status, setStatus] = useState("");
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(true);
  const [pageMessage, setPageMessage] = useState("");
  const { confirm, ConfirmDialog } = useConfirmDialog();

  async function load(filters = { Status: status, Keyword: keyword, Page: pagination.Page, PerPage: pagination.PerPage }) {
    setLoading(true);
    setPageMessage("");
    try {
      const me = await getCurrentAdmin();
      setCurrentUser(me.User);
      const data = await listAdminNewsletterCampaigns(filters);
      setItems(data.Items);
      setCounts(data.Counts || { Draft: 0, Sending: 0, Sent: 0 });
      setPagination(data.Pagination || defaultPagination);
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "電子報資料載入失敗。");
      navigate("/admin-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load({ Status: "", Keyword: "", Page: 1, PerPage: pagination.PerPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    load({ Status: status, Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage });
  }

  async function handleDelete(item: AdminNewsletterCampaign) {
    const confirmed = await confirm({
      title: "刪除電子報？",
      description: (
        <>
          確定要刪除 <strong className="font-bold text-[#203d32]">{item.Subject}</strong> 嗎？
          <br />
          刪除後無法復原，若已開始寄送則無法刪除。
        </>
      ),
      confirmText: "刪除電子報",
      cancelText: "取消",
      tone: "danger",
    });
    if (!confirmed) return;

    try {
      await deleteAdminNewsletterCampaign(item.CampaignId);
      await load({ Status: status, Keyword: keyword.trim(), Page: pagination.Page, PerPage: pagination.PerPage });
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "電子報刪除失敗。");
    }
  }

  async function handleDuplicate(item: AdminNewsletterCampaign) {
    try {
      const saved = await saveAdminNewsletterCampaign(null, {
        Subject: `${item.Subject}（複製）`,
        PreviewText: item.PreviewText || undefined,
        HtmlContent: item.HtmlContent,
      });
      navigate(`/admin/newsletter-campaigns/${saved.Item.CampaignId}/edit`);
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "電子報複製失敗。");
    }
  }

  const allItemCount = counts.Draft + counts.Sending + counts.Sent || pagination.Total;

  return (
    <AdminLayout sectionLabel="內容管理" title="電子報寄送" user={currentUser} onLogout={handleLogout}>
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex gap-2 overflow-x-auto pb-1 md:hidden [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {[
            { value: "", label: "全部", count: allItemCount, tone: "text-[#315746]" },
            { value: "Draft", label: "草稿", count: counts.Draft, tone: "text-[#59665f]" },
            { value: "Sending", label: "寄送中", count: counts.Sending, tone: "text-[#7a5610]" },
            { value: "Sent", label: "已寄出", count: counts.Sent, tone: "text-[#245438]" },
          ].map((option) => (
            <button
              className={`flex min-w-[96px] shrink-0 items-center justify-between rounded-md border px-3 py-2 text-left transition ${status === option.value ? "border-[#315746] bg-[#315746] text-white" : `border-[#dfe6e1] bg-white ${option.tone}`}`}
              key={option.value}
              onClick={() => {
                setStatus(option.value);
                load({ Status: option.value, Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage });
              }}
              type="button"
            >
              <span className="text-xs font-bold">{option.label}</span>
              <span className="ml-3 text-lg font-bold">{option.count}</span>
            </button>
          ))}
        </div>

        <div className="hidden gap-3 md:grid md:flex-1 md:grid-cols-4">
          <button className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3 text-left hover:border-[#b8c8bf]" onClick={() => { setStatus(""); load({ Status: "", Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage }); }} type="button">
            <p className="text-xs font-bold text-[#7b877f]">全部</p>
            <p className="mt-1 text-2xl font-bold text-[#203d32]">{allItemCount}</p>
          </button>
          <button className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3 text-left hover:border-[#b8c8bf]" onClick={() => { setStatus("Draft"); load({ Status: "Draft", Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage }); }} type="button">
            <p className="text-xs font-bold text-[#7b877f]">草稿</p>
            <p className="mt-1 text-2xl font-bold text-[#59665f]">{counts.Draft}</p>
          </button>
          <button className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3 text-left hover:border-[#b8c8bf]" onClick={() => { setStatus("Sending"); load({ Status: "Sending", Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage }); }} type="button">
            <p className="text-xs font-bold text-[#7b877f]">寄送中</p>
            <p className="mt-1 text-2xl font-bold text-[#7a5610]">{counts.Sending}</p>
          </button>
          <button className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3 text-left hover:border-[#b8c8bf]" onClick={() => { setStatus("Sent"); load({ Status: "Sent", Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage }); }} type="button">
            <p className="text-xs font-bold text-[#7b877f]">已寄出</p>
            <p className="mt-1 text-2xl font-bold text-[#245438]">{counts.Sent}</p>
          </button>
        </div>
      </div>

      <form className="mb-5 grid gap-3 rounded-md border border-[#dfe6e1] bg-white p-3 md:sticky md:top-[102px] md:z-10 md:flex md:flex-wrap md:items-end md:bg-white/95 md:p-4 md:shadow-[0_10px_24px_rgba(32,61,50,.08)] md:backdrop-blur" onSubmit={handleSearch}>
        <div className="grid min-w-0 gap-1 md:min-w-[120px]">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="campaign-status">狀態</label>
          <select id="campaign-status" className="h-11 rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">全部</option>
            <option value="Draft">草稿</option>
            <option value="Sending">寄送中</option>
            <option value="Sent">已寄出</option>
          </select>
        </div>
        <div className="grid min-w-0 gap-1 md:min-w-[260px] md:flex-1">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="campaign-keyword">關鍵字</label>
          <input id="campaign-keyword" className="h-11 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="搜尋主旨" value={keyword} onChange={(event) => setKeyword(event.target.value)} />
        </div>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32]">
          <Search size={18} />
          搜尋
        </button>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => load({ Status: status, Keyword: keyword.trim(), Page: pagination.Page, PerPage: pagination.PerPage })} type="button">
          <RefreshCw size={18} />
          重新整理
        </button>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32]" onClick={() => navigate("/admin/newsletter-campaigns/new")} type="button">
          <Plus size={20} />
          新增電子報
        </button>
      </form>

      {pageMessage && <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{pageMessage}</div>}

      <div className="rounded-md border border-[#dfe6e1] bg-white">
        <div className="max-h-[62vh] overflow-auto">
          <table className="w-full min-w-[960px] table-fixed border-collapse text-left">
            <thead className="sticky top-0 z-10 bg-[#f0f3f1] text-sm text-[#59665f] shadow-[inset_0_-1px_0_#dfe6e1]">
              <tr>
                <th className="w-[360px] px-4 py-3">主旨</th>
                <th className="w-[100px] px-4 py-3">狀態</th>
                <th className="w-[160px] px-4 py-3">寄送進度</th>
                <th className="w-[150px] px-4 py-3">寄出時間</th>
                <th className="w-[190px] px-4 py-3">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={5}>載入中...</td></tr>
              ) : items.length === 0 ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={5}>沒有符合條件的電子報。</td></tr>
              ) : items.map((item) => (
                <tr className="border-t border-[#edf0ee]" key={item.CampaignId}>
                  <td className="px-4 py-4">
                    <strong className="block truncate text-[#203d32]">{item.Subject}</strong>
                    {item.PreviewText && <span className="block truncate text-xs text-[#9a8a7a]">{item.PreviewText}</span>}
                  </td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${statusBadgeClass[item.Status]}`}>{statusLabels[item.Status]}</span>
                  </td>
                  <td className="px-4 py-4 text-sm">
                    {item.Status === "Draft" ? (
                      <span className="text-[#9a8a7a]">尚未寄送</span>
                    ) : (
                      <span>
                        {item.SentCount + item.FailedCount} / {item.TotalRecipients}
                        {item.FailedCount > 0 && <span className="ml-1 text-[#9a2d2d]">（失敗 {item.FailedCount}）</span>}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-4 text-sm">{item.SentAt || "-"}</td>
                  <td className="px-4 py-4">
                    <div className="grid gap-2">
                      {item.Status === "Draft" ? (
                        <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-3 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => navigate(`/admin/newsletter-campaigns/${item.CampaignId}/edit`)} type="button">
                          <Edit size={16} />
                          編輯 / 寄送
                        </button>
                      ) : (
                        <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-3 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => navigate(`/admin/newsletter-campaigns/${item.CampaignId}/edit`)} type="button">
                          <Send size={16} />
                          查看進度
                        </button>
                      )}
                      <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-3 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => handleDuplicate(item)} type="button">
                        <Copy size={16} />
                        複製
                      </button>
                      {item.Status !== "Sending" && (
                        <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#f2b8b5] bg-white px-3 font-bold text-[#b3261e] transition hover:bg-[#fdebea]" onClick={() => handleDelete(item)} type="button">
                          <Trash2 size={16} />
                          刪除
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination disabled={loading} pagination={pagination} onChange={(page) => load({ Status: status, Keyword: keyword.trim(), Page: page, PerPage: pagination.PerPage })} />
      </div>

      <ConfirmDialog />
    </AdminLayout>
  );
}
